import axios from "axios";
import { store } from "@/store";
import { captureException, addBreadcrumb } from "@/config/sentry";

// API Base URL should be the backend URL through proxy
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/postgres";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Crucial for sending cookies
  timeout: 120000, // Augmenté à 2 minutes pour les conversations longues
});

// Interceptor pour ajouter la langue dans les headers de chaque requête
apiClient.interceptors.request.use(
  (config) => {
    // Récupérer la langue courante depuis Redux store
    const state = store.getState();
    const language = state.settings.language;

    if (language) {
      config.headers["Accept-Language"] = language;
      config.headers["X-User-Language"] = language;
    }

    // ✅ Ajouter un breadcrumb Sentry pour tracer les appels API
    addBreadcrumb(
      `API Request: ${config.method?.toUpperCase()} ${config.url}`,
      {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
      },
    );

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// You can add interceptors here for global error handling or token refresh logic if needed later
apiClient.interceptors.response.use(
  (response) => {
    // ✅ Ajouter un breadcrumb pour les réponses réussies
    addBreadcrumb(
      `API Response: ${response.config.method?.toUpperCase()} ${
        response.config.url
      } - ${response.status}`,
      {
        status: response.status,
        method: response.config.method,
        url: response.config.url,
      },
    );
    return response;
  },
  (error) => {
    // ✅ Capturer l'erreur dans Sentry avec contexte détaillé
    if (error.response) {
      // Erreur HTTP (4xx, 5xx)
      const errorContext = {
        http_status: error.response.status,
        http_method: error.config?.method,
        http_url: error.config?.url,
        http_base_url: error.config?.baseURL,
        response_data: error.response.data,
      };

      // Ne pas capturer les 401 (non autorisé) car c'est un flux normal
      // Ne pas capturer les 404 sauf si c'est critique
      if (error.response.status >= 500) {
        captureException(error, errorContext);
      }

      // Ajouter un breadcrumb pour les erreurs HTTP
      addBreadcrumb(
        `API Error: ${error.config?.method?.toUpperCase()} ${
          error.config?.url
        } - ${error.response.status}`,
        errorContext,
      );
    } else if (error.request) {
      // Erreur réseau (pas de réponse)
      const errorContext = {
        error_type: "network_error",
        http_method: error.config?.method,
        http_url: error.config?.url,
        message: error.message,
      };

      captureException(error, errorContext);
      addBreadcrumb("API Network Error", errorContext);
    } else {
      // Erreur de configuration de la requête
      const errorContext = {
        error_type: "request_setup_error",
        message: error.message,
      };

      captureException(error, errorContext);
      addBreadcrumb("API Setup Error", errorContext);
    }

    // Handle errors globally
    if (error.response && error.response.status === 401) {
      // e.g., redirect to login or dispatch logout action
      // console.error("Unauthorized, redirecting to login...");
      // if (!window.location.href.includes("/login")) {
      //   window.location.href = "/login";
      // }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
